import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { Button, ButtonGroup, Grid, Loader } from 'semantic-ui-react';
import Analysis from '../../api/analysis';
import { UIDateFormat } from '../../config/dates';
import { checkForUpdates, showSuccessAlert, updateAnalysisView } from '../../redux/actions';
import { graphInitialState, gridInitialState } from '../../redux/reducers/analysisViewReducer';
import AnalysisFilters from '../analysis/AnalysisFilters';
import RevenueGraph from '../revenue/RevenueGraph';
import DataGrid from '../shared/DataGrid';
import _ from 'lodash';
import { prettyMoney } from '../../helpers/currency';

class AnalysisView extends React.Component {

  constructor() {
    super();
    this.graphDebounce = _.debounce(this.loadGraph, 850);
  }

  handleRefresh = () => {
    const { startDate, endDate, selectedCategory, selectedMeasure, selectedTotalBy, selectedTimeGroupBy, selectedDataGroup, selectedDayOfWeek } = this.props.analysis;
    const { operator, company, subcompany, site } = this.props.analysisFilters.filterSelected

    this.props.updateAnalysisView({ loading: true, graph: graphInitialState }).then(() => {
      Analysis.getGrid(
        moment(startDate, UIDateFormat).toISOString(),
        moment(endDate, UIDateFormat).toISOString(),
        selectedMeasure,
        selectedTotalBy,
        selectedTimeGroupBy,
        selectedDataGroup,
        selectedCategory,
        operator,
        company.id,
        subcompany.id,
        site.id,
        selectedDayOfWeek
      )
      .then(
        grid => {
          this.props.updateAnalysisView({ grid, loading: false})
          
        },
        () => this.props.updateAnalysisView({ loading: false, grid: gridInitialState })
      );
    })
  }

  handleExport = () => {
    const { startDate, endDate, selectedMeasure, selectedCategory, selectedTotalBy, selectedTimeGroupBy, selectedDataGroup, selectedDayOfWeek } = this.props.analysis;
    const { operator, company, subcompany, site } = this.props.analysisFilters.filterSelected
    const { email } = this.props.login;

    this.props.updateAnalysisView({ loading: true }).then(() => {
      Analysis.getExport(
        email,
        moment(startDate, UIDateFormat).toISOString(),
        moment(endDate, UIDateFormat).toISOString(),
        selectedMeasure,
        selectedTotalBy,
        selectedTimeGroupBy,
        selectedDataGroup,
        selectedCategory,
        operator,
        company.id,
        subcompany.id,
        site.id,
        selectedDayOfWeek
      )
      .then(
        () => this.props.showSuccessAlert("An email containing an export of this screen has been emailed to you!").then(() => this.props.updateAnalysisView({ loading: false })),
        () => this.props.updateAnalysisView({ loading: false })
      );
    })

  }

  loadGraph = () => {
    const { timeGroupBy, startDate, endDate, selectedRows, selectedCategory, selectedMeasure, selectedTotalBy, selectedTimeGroupBy, selectedDataGroup, selectedDayOfWeek } = this.props.analysis;
    const { operator, company, subcompany, site } = this.props.analysisFilters.filterSelected
    const { canGraph } = timeGroupBy.find(x => x.id === selectedTimeGroupBy) || false;
    this.props.updateAnalysisView({ graph: graphInitialState, graphLoading: true }).then(() => {
      if (canGraph && selectedRows.length > 0) {
        const { key, category } = selectedRows[0];
        Analysis.getGraph(
          selectedRows,
          moment(startDate, UIDateFormat).toISOString(),
          moment(endDate, UIDateFormat).toISOString(),
          selectedMeasure,
          selectedTotalBy,
          selectedTimeGroupBy,
          selectedDataGroup,
          selectedCategory,
          operator,
          company.id,
          subcompany.id,
          site.id,
          selectedDayOfWeek
        )
        .then(
          graph => this.props.updateAnalysisView({ graph, graphLoading: false }),
          () => this.props.updateAnalysisView({ graphLoading: false, graph: graphInitialState })
        );
      } else {
        this.props.updateAnalysisView({ graphLoading: false, graph: graphInitialState })
      }

    });
  }

  onRowClick = selectedRow => {
    const { selectedRows, graphType } = this.props.analysis
    let newSelectedRows = [];

    if (selectedRows.includes(selectedRow)) {
      newSelectedRows = [...selectedRows.filter(row => row !== selectedRow)]
    } else {
      if (graphType == 1) {
        newSelectedRows = [selectedRow];
      } else {
        if (selectedRows.length < 5) {
          newSelectedRows = [...selectedRows, selectedRow];
        }
      }
    }
    this.props.updateAnalysisView({ selectedRows: newSelectedRows }).then(() => graphType == 1 ? this.loadGraph() : this.graphDebounce())
  }

  componentDidUpdate(prevProps) {
    const { operator } = this.props.analysisFilters.filterSelected;
    if (operator !== prevProps.analysisFilters.filterSelected.operator) {
      Analysis.getMetaData(operator).then(response => {
        const data = {
          ...response, 
          selectedTimeGroupBy: response.timeGroupBy[0] ? response.timeGroupBy[0].id : null,
          selectedTotalBy: response.totalBy[0] ? response.totalBy[0].id : null,
          selectedMeasure: response.measures[0] ? response.measures[0].id : null,
          selectedDataGroup: response.dataGroups[0] ? response.dataGroups[0].id : null,
          selectedDayOfWeek: [...response.daysOfWeek.map(x => x.id)],
          selectedCategory: [...response.categories.map(x => x.id)],
        }
        this.props.updateAnalysisView(data);
      })
    }
    
    if (checkForUpdates(prevProps.analysisFilters, this.props.analysisFilters)) {
      this.props.updateAnalysisView({
          graph: graphInitialState,
          grid: gridInitialState
      })
    }
  }

  render() {

    const { grid, graphType, graph, graphLoading, loading, selectedRows } = this.props.analysis;
    const formatter = (value) => isNaN(value) ? value : prettyMoney(value, 0);

    return (
      <Grid className="height-100 gs-flex-container-vertical" padded="vertically">
        <Grid.Row>
          <Grid.Column width="16">
            <AnalysisFilters 
              onRefresh={this.handleRefresh}
              onExport={this.handleExport}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width="16" verticalAlign='middle'>
            <Loader active={graphLoading} />
            <div className="flex-vertical h-100">
              <div className="flex-shrink mr-1" style={{ textAlign: "center" }}>
                <div className="mb-0_5" style={{ fontWeight: "bold" }}>Graph Mode:</div>
                <ButtonGroup vertical basic size="small">
                  <Button active={graphType == 1} onClick={() => this.props.updateAnalysisView({ graphType: 1, selectedRows: [], graph: graphInitialState })}>Standard</Button>
                  <Button active={graphType == 2} onClick={() => this.props.updateAnalysisView({ graphType: 2 })}>MultiSelect</Button>
                </ButtonGroup>
              </div>
              <div className='flex-stretch flex-container'>
                <div style={{ border: "1px solid #ddd", padding: "1em 1em 0em 2em" }}>
                  <RevenueGraph 
                    tickFormatter={formatter}
                    toolTipFormatter={formatter}
                    data={graph.data}
                    lines={graph.lines}
                    symbol={""} 
                    aspect={4}
                  />
                </div>
              </div>
            </div>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row className="flex-stretch">
          <Grid.Column width="16" className="gs-flex-container-vertical">
            <small style={{ float: "right"}}>{selectedRows.length} of { graphType == 1 ? 1 : 5 } selected</small>
            <DataGrid
              loading={loading}
              headings={grid.headerDef}
              data={grid.data}
              rowDef={grid.rowDef}
              activeRow={selectedRows}
              onRowClick={this.onRowClick}
              fullHeight
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );

  }
}

const mapStateToProps = state => {
	return {
    analysis: state.analysisView,
    analysisFilters: state.analysisFilters,
    login: state.logIn
  };
};

export default connect(mapStateToProps, { updateAnalysisView, showSuccessAlert })(AnalysisView);
